import React, { useRef, useState } from "react";
import "../style.css";
import { ArrowRepeat, CameraVideo } from "react-bootstrap-icons";
import { v4 as uuidv4 } from "uuid";
const uuid = uuidv4();
// Upload helper as before
async function uploadVideoToServer(videoFile, session) {
  const formData = new FormData();
  formData.append("video", videoFile);
  const response = await fetch(
    `https://api.edit.guru-x.de/save_video/${session}`,
    {
      method: "POST",
      body: formData,
    }
  );
  if (!response.ok) {
    throw new Error("Video upload failed");
  }
  const data = await response.json();
  const match = data.video_code.match(/videoUrl="([^"]+)"/);
  if (match) {
    return match[1];
  } else {
    throw new Error("Server did not return a video filename");
  }
}

// Extract YouTube embed URL
function getYouTubeEmbedUrl(url) {
  const regExp =
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regExp);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}
// Extract Vimeo embed URL
function getVimeoEmbedUrl(url) {
  // Handles: vimeo.com/VIDEO_ID or player.vimeo.com/video/VIDEO_ID
  const regExp = /vimeo\.com\/(?:video\/)?(\d+)/;
  const match = url.match(regExp);
  return match ? `https://player.vimeo.com/video/${match[1]}` : null;
}

function VideoGenerator({ openModal, session }) {
  const [videoFile, setVideoFile] = useState(null);
  const [videoSrc, setVideoSrc] = useState(""); // filename OR URL
  const [inputType, setInputType] = useState("url"); // 'upload' | 'url'
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sizePercent, setSizePercent] = useState(100);
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef(null);

  // File upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    setVideoSrc("");
    setVideoFile(null);
    if (!file) return;
    setUploading(true);
    try {
      const filename = await uploadVideoToServer(file, session);
      setVideoFile(file);
      setVideoSrc(filename);
    } catch (err) {
      alert("Video upload failed: " + err.message);
    }
    setUploading(false);
  };
  const handleUrlChange = (e) => setVideoSrc(e.target.value);

  // Generate JSX code
  const generateComponentCode = () => {
    let src =
      inputType === "upload" && videoSrc
        ? `https://api.edit.guru-x.de/preview/${session}/${videoSrc}`
        : videoSrc;
    let jsxCode = "";

    // Detect YouTube or Vimeo
    const ytEmbedUrl = inputType === "url" ? getYouTubeEmbedUrl(src) : null;
    const vimeoEmbedUrl =
      inputType === "url" && !ytEmbedUrl ? getVimeoEmbedUrl(src) : null;

    if (ytEmbedUrl) {
      jsxCode =
        `<div id="${uuid}" />` +
        `<figure className="pb-10 flex flex-col items-center justify-center mx-auto  py-10 ">
        <iframe
          width="560"
          height="315"
          src="${ytEmbedUrl}"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-top-navigation-by-user-activation"
          style={{ width: "${sizePercent}%" }}
        ></iframe>
        ${
          caption
            ? `<figcaption className="w-full mx-auto text-gray-600 text-center">${caption.replace(
                /"/g,
                "&quot;"
              )}</figcaption>`
            : ""
        }
      </figure>`;
    } else if (vimeoEmbedUrl) {
      jsxCode =
        `<div id="${uuid}" />` +
        `<figure className="pb-10 flex flex-col items-center justify-center mx-auto  py-10 ">
        <iframe
          width="560"
          height="315"
          src="${vimeoEmbedUrl}"
          title="Vimeo video player"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-top-navigation-by-user-activation"
          style={{ width: "${sizePercent}%" }}
        ></iframe>
        ${
          caption
            ? `<figcaption className="w-full mx-auto text-gray-600 text-center">${caption.replace(
                /"/g,
                "&quot;"
              )}</figcaption>`
            : ""
        }
      </figure>`;
    } else {
      jsxCode =
        `<div id="${uuid}" />` +
        `<figure className="pb-10 flex flex-col items-center justify-center mx-auto  py-10 ">
        <video src="${src}" style={{ width: "${sizePercent}%" }} controls />
        ${
          caption
            ? `<figcaption className="w-full mx-auto text-gray-600 text-center">${caption.replace(
                /"/g,
                "&quot;"
              )}</figcaption>`
            : ""
        }
      </figure>`;
    }

    setResult(jsxCode);
    openModal &&
      openModal(
        "{/*//For security reasons, displaying the video under guru-x.de is not allowed.\n//To view the updated module and the video in the test environment, select 'Save & open preview' from the editor menu.*/}\n\n" +
          jsxCode
      );
  };

  const resetAll = () => {
    setVideoFile(null);
    setVideoSrc("");
    setCaption("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const previewSrc =
    inputType === "upload" && videoSrc
      ? `https://api.edit.guru-x.de/preview/${session}/${videoSrc}`
      : inputType === "url"
      ? videoSrc
      : "";

  const ytPreviewUrl =
    inputType === "url" && videoSrc ? getYouTubeEmbedUrl(videoSrc) : null;
  const vimeoPreviewUrl =
    inputType === "url" && videoSrc && !ytPreviewUrl
      ? getVimeoEmbedUrl(videoSrc)
      : null;

  return (
    <div className="grid place-items-center m-8 p-8 items-center">
      <div className="flex flex-col w-full h-full items-center">
        <div className="my-4 items-center text-center">
          <div className="text-sm m-2">
            Supported: MP4, WEBM, MOV, YouTube & Vimeo URLs
          </div>
          <div className="flex flex-row items-center gap-4 justify-center mb-2">
            <label>
              <input
                type="radio"
                value="url"
                checked={inputType === "url"}
                onChange={() => {
                  setInputType("url");
                  setVideoSrc("");
                  setVideoFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                disabled={uploading}
              />{" "}
              Video URL (Direct, YouTube or Vimeo)
            </label>{" "}
            <label>
              <input
                type="radio"
                value="upload"
                checked={inputType === "upload"}
                onChange={() => {
                  setInputType("upload");
                  setVideoSrc("");
                  setVideoFile(null);
                }}
                //disabled={uploading}
                disabled
              />{" "}
              Video Upload (Comming soon)
            </label>
          </div>
          {inputType === "upload" ? (
            <input
              id="videoInput"
              ref={fileInputRef}
              type="file"
              className="file-input file-input-bordered file-input-primary w-full max-w-xs"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleVideoUpload}
              disabled={uploading}
            />
          ) : (
            <input
              type="text"
              placeholder="Paste a video URL (mp4/webm/mov, YouTube or Vimeo)"
              className="input input-bordered w-full "
              value={videoSrc}
              onChange={handleUrlChange}
              disabled={uploading}
            />
          )}

          <div className="my-5 flex items-center justify-center">
            <label htmlFor="range" className="mr-3">
              Relative width: {`${sizePercent}%`}
            </label>
            <input
              id="range"
              type="range"
              min={0}
              max="100"
              value={sizePercent}
              onChange={(e) => setSizePercent(Number(e.target.value))}
              className="range range-primary range-sm mx-4 w-44"
            />
          </div>
          <div className="h-5"></div>

          {/* Preview */}
          {previewSrc && (
            <div
              className="relative mx-auto w-fit"
              style={{
                width: `${sizePercent}%`,
                minWidth: "1px",
                maxWidth: "100%",
              }}
            >
              <div
                className="bg-slate-200 flex items-center justify-center"
                style={{ width: "100%", minHeight: "315px" }}
              >
                <CameraVideo className="justify-self-center text-center w-10 h-10"></CameraVideo>
              </div>
              {/**  {ytPreviewUrl ? (
                <iframe
                  src={ytPreviewUrl}
                  width="560"
                  height="315"
                  title="YouTube preview"
                  style={{
                    width: "100%",
                    aspectRatio: "16/9",
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                />
              ) : vimeoPreviewUrl ? (
                <iframe
                  src={vimeoPreviewUrl}
                  width="560"
                  height="315"
                  title="Vimeo preview"
                  style={{
                    width: "100%",
                    aspectRatio: "16/9",
                  }}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              ) : (
                <video
                  src={previewSrc}
                  controls
                  style={{
                    maxWidth: "100%",
                    borderRadius: "8px",
                    width: "100%",
                  }}
                  preload="auto"
                />
              )}*/}
            </div>
          )}
        </div>
        <div className="h-5"></div>
        <textarea
          className="text-sm shadow appearance-none border rounded w-10/12 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          type="text"
          placeholder="Enter a caption..."
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          disabled={uploading}
        />
        <div className="flex flex-row m-auto pt-10">
          <button
            className="btn btn-square min-w-40 mx-4"
            onClick={generateComponentCode}
            disabled={!videoSrc}
          >
            Create
          </button>
          <button
            id="resetButton"
            type="reset"
            onClick={resetAll}
            className="btn btn-circle m-auto"
          >
            <ArrowRepeat size={30} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default VideoGenerator;
